import React, { useEffect, useState } from 'react';
import { Nav, Collapse, Image } from 'react-bootstrap';
import { List, House, PeopleFill, ArrowLeftRight, Collection, BarChartFill, FileTextFill, BoxSeam, ClipboardData } from 'react-bootstrap-icons';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { refreshUserData } from '../../services/auth-service';
import config from '../../config';
import godrejLogo from '../../assets/images/godrej-logo-dark.svg';
import logoutarrow from '../../assets/images/logout.svg';

export default function SideBar({ sidebar, setSidebar }) {
  const [open, setOpen] = useState({});
  const [authorizedSessions, setAuthorizedSessions] = useState({});
  const { instance } = useMsal();
  const navigate = useNavigate();

  const menuIcons = {
    'Home': <House size={20} />,
    'Admin': <PeopleFill size={20} />,
    'Transactions': <ArrowLeftRight size={20} />,
    'Masters': <Collection size={20} />,
    'Reports': <BarChartFill size={20} />,
    'Logging': <FileTextFill size={20} />,
    'IOD': <BoxSeam size={20} />,
    'BOD': <ClipboardData size={20} />
  };

  useEffect(() => {
    if (sidebar === 'closed') {
      setOpen({});
    }
  }, [sidebar]);

  useEffect(() => {
    setMenu();
    
    const handleMenuUpdate = async () => {
      try {
        await refreshUserData();
        setMenu();
      } catch (error) {
        console.error('Error refreshing menu data:', error);
      }
    };
    
    const handleRoleUpdate = () => {
      setMenu();
    };
    
    window.addEventListener('menuUpdated', handleMenuUpdate);
    window.addEventListener('roleUpdated', handleRoleUpdate);
    
    return () => {
      window.removeEventListener('menuUpdated', handleMenuUpdate);
      window.removeEventListener('roleUpdated', handleRoleUpdate);
    };
  }, []);

  const setMenu = () => {
    try {
      const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
      const authorizedSessionsTemp = {};
      
      if (userData.authorizedSessions) {
        for (let key in userData.authorizedSessions) {
          const menuItems = userData.authorizedSessions[key];
          const readableItems = menuItems.filter(item => item.read === true);
          
          if (readableItems.length > 0) {
            authorizedSessionsTemp[key] = readableItems;
          }
        }
      }
      
      setAuthorizedSessions(authorizedSessionsTemp);
    } catch (error) {
      console.error('Error setting menu:', error);
    }
  };

  const handleLogout = async () => {
    const token = sessionStorage.getItem('Token');
    sessionStorage.setItem('_loggingOut', 'true');
    const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || 'http://localhost:3001';
    try {
      await fetch(apiEndpoint + '/login/logout', { method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token } });
    } catch (e) { console.error('LOGOUT FETCH ERROR:', e); }
    sessionStorage.clear();
    sessionStorage.setItem('_loggingOut', 'true');
    instance.logoutRedirect({ postLogoutRedirectUri: `${window.location.origin}` }).catch(() => {
      window.location.href = '/';
    });
  };

  const handleMenuClick = (menuName) => {
    if (sidebar === 'closed') {
      setSidebar('open');
    }
    setOpen(prev => {
      const newState = {};
      newState[menuName] = !prev[menuName];
      return newState;
    });
  };

  const handleSubmenuClick = () => {
    setSidebar('closed');
  };

  return (
    <>
      <div className="sidebar-head">
        <List 
          className="head-toggle" 
          onClick={() => setSidebar(sidebar === 'closed' ? 'open' : 'closed')}
        />
        <div className="user-section">
          <div className="user-info">
            <span className="user-name">{sessionStorage.getItem('UserName') || 'User'}</span>
            <span className="user-email">{sessionStorage.getItem('Email') || ''}</span>
          </div>
          <Image src={logoutarrow} onClick={handleLogout} style={{ cursor: 'pointer', width: '20px' }} />
        </div>
      </div>
      
      <div className={`sidebar-body ${sidebar}`}>
        <Nav bsPrefix="nav-list">
          <Nav.Item className="nav-item-parent">
            <Nav.Link as={Link} to="/dashboard" className="nav-link-parent" onClick={handleSubmenuClick}>
              <span className="menu-icon">{menuIcons['Home']}</span>
              <span className="menu-text">Home</span>
            </Nav.Link>
          </Nav.Item>

          {Object.keys(authorizedSessions).map((parentMenu, index) => (
            <React.Fragment key={index}>
              <Nav.Item className="nav-item-parent">
                <Nav.Link 
                  className="nav-link-parent" 
                  onClick={() => handleMenuClick(parentMenu)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="menu-icon">{menuIcons[parentMenu] || <Collection size={20} />}</span>
                  <span className="menu-text">{parentMenu}</span>
                  <span className="arrow">{open[parentMenu] ? '▼' : '▶'}</span>
                </Nav.Link>
              </Nav.Item>
              
              <Collapse in={open[parentMenu]}>
                <div>
                  {authorizedSessions[parentMenu].map((item, idx) => (
                    <Nav.Item key={idx} className="nav-item-child">
                      <Nav.Link 
                        as={Link} 
                        to={item.menuUrl || '#'} 
                        className="nav-link-child"
                        onClick={handleSubmenuClick}
                      >
                        {item.submenu}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </div>
              </Collapse>
            </React.Fragment>
          ))}

          {((() => {
            const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
            const role = userData.roleCode || userData.role || '';
            return role === 'SYSADMIN';
          })()) && (
            <Nav.Item className="nav-item-parent">
              <Nav.Link as={Link} to="/logging" className="nav-link-parent" onClick={handleSubmenuClick}>
                <span className="menu-icon">{menuIcons['Logging']}</span>
                <span className="menu-text">Logging</span>
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
        
        <div className="footer">
          <Image className="footer-logo" src={godrejLogo} />
        </div>
      </div>
    </>
  );
}
